import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import {
	GameHover,
	GameHoverTop,
	GameHoverBottom,
	GameName,
	ProviderName,
	PlayButton
} from '~/components/casino-shared/game-card/game-card-styles';
import EnhancedImage, { Image } from '~/components/casino-shared/tournaments/enhanced-image';
import Icon, { ICON_CIRCLE_FILLED, ICON_REMAINING, ICON_TROPHY_LINE } from '~/components/icon';
import useTranslations from '~/components/translation/use-translations';
import useTimeTicker from '~/components/casino-shared/tournaments/utils/use-time-ticker';
import useConditionalPrizePool from '~/components/casino-shared/tournaments/utils/use-conditional-prize-pool';
import useTournamentTags from '~/components/casino-shared/tournaments/utils/use-tournament-tags';
import usePushTransformedPath from '~/components/casino-shared/tournaments/utils/use-push-transformed-path';
import trackTournamentEvent from '~/components/casino-shared/tournaments/utils/track-tournament-event';
import {
	preload,
	loadTournamentDetails,
	loadTournamentLobby,
	loadTournamentsQueryClientProvider,
	loadTournamentDetailsLeaderboard
} from '~/components/casino-shared/tournaments/async';
import breakpoint from '~/utils/style-utils';
import { ROUTE_TOURNAMENT_LOBBY } from '~/components/casino-shared/tournaments/routes';

const HOVER_BUTTON_TEXT_KEY = 'TOURNAMENTS_CARD_HOVER_BUTTON_TEXT';
export const TOURNAMENT_CARD_WIDTH = 336;
export const TOURNAMENT_CARD_HEIGHT = 220;

const Container = styled.div`
	position: relative;
	width: ${TOURNAMENT_CARD_WIDTH}px;
	height: ${TOURNAMENT_CARD_HEIGHT}px;
	border-radius: 12px;
	overflow: hidden;

	img {
		pointer-events: none;
		touch-action: none;
	}

	${breakpoint.lg`
		&:hover ${GameHover} {
			z-index: unset;
			display: flex;
			background: rgba(0, 0, 0, 0.4);
			backdrop-filter: blur(25px);
		}
	`}
`;

const InfoBar = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 6px;
	height: 60px;
	background: rgba(0, 0, 0, 0.3);
	background-blend-mode: overlay;
	backdrop-filter: blur(20px);
	padding: 0 15px;
`;

const InfoBarCell = styled.div<{ right?: boolean }>`
	flex: 1;
	overflow: ${({ right }) => (right ? 'unset' : 'hidden')};
`;

const InfoBarTitle = styled.div`
	font: var(--tournaments-label-bold, var(--label-bold));
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: var(--tournaments-text-on-tag);
`;

const InfoBarPrize = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 3px;
	font: var(--tournaments-label-bold, var(--label-bold));
	overflow: hidden;

	svg {
		flex-shrink: 0;
		width: 16px;
		height: 16px;
		color: var(--tournaments-text-on-tag);
	}

	span {
		color: var(--tournaments-text-on-tag);
		white-space: nowrap;
		overflow: hidden;
	}
`;

const InfoBarDesc = styled.div`
	font: var(--tournaments-small, var(--small));
	color: var(--tournaments-text-on-tag);
	white-space: nowrap;
`;

const InfoBarTicker = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 3px;
	font: var(--tournaments-small-bold, var(--small-bold));

	svg {
		flex-shrink: 0;
		width: 12px;
		height: 12px;
		color: var(--tournaments-text-on-tag);
	}

	span {
		color: var(--tournaments-text-on-tag);
		white-space: nowrap;
		overflow: hidden;
	}
`;

interface TournamentCardProps {
	id: number;
	name: string;
	image: Image;
	otherView?: Image;
	tags: string[];
	prizePool?: number;
	prizePoolText?: string;
	currencyCode: string;
	startDate: string;
	endDate: string;
	isJoined: boolean;
}

export default function TournamentCard({
	id,
	name,
	image,
	otherView,
	tags,
	prizePool,
	prizePoolText,
	currencyCode,
	startDate,
	endDate,
	isJoined
}: TournamentCardProps) {
	const { hasStarted, hasEnded, timeText } = useTimeTicker(startDate, endDate);
	const conditionalPrizePool = useConditionalPrizePool(prizePoolText, prizePool, currencyCode);
	const tournamentTags = useTournamentTags(tags);
	const { translations } = useTranslations([HOVER_BUTTON_TEXT_KEY]);
	const isSmall = useSelector(isSmallerSelector);
	const pushTransformedPath = usePushTransformedPath();

	const goDetails = () => {
		trackTournamentEvent('explore-page', 'tournament-card-click', {
			tournamentName: name,
			tournamentPrize: conditionalPrizePool,
			isPlayerJoined: isJoined
		});

		pushTransformedPath(`${ROUTE_TOURNAMENT_LOBBY}/${id}`);
	};

	const preloadDetails = () => {
		preload(
			loadTournamentLobby(),
			loadTournamentsQueryClientProvider(),
			loadTournamentDetails(),
			loadTournamentDetailsLeaderboard()
		);
	};

	return (
		<Container
			onClick={isSmall ? goDetails : undefined}
			onMouseEnter={preloadDetails}
			onTouchStart={preloadDetails}
			data-at="tournament-card"
		>
			<EnhancedImage image={image} otherView={otherView} alt={name} sizes={`${TOURNAMENT_CARD_WIDTH}px`} />

			<InfoBar>
				<InfoBarCell>
					<InfoBarTitle data-at="name">{name}</InfoBarTitle>
					<InfoBarDesc data-at="tags">{tournamentTags}</InfoBarDesc>
				</InfoBarCell>

				<InfoBarCell right>
					<InfoBarPrize>
						<Icon type={ICON_TROPHY_LINE} />
						<span data-at="prize-pool">{conditionalPrizePool}</span>
					</InfoBarPrize>
					{timeText && (
						<InfoBarTicker data-at="time-text">
							{!hasEnded && <Icon type={hasStarted ? ICON_CIRCLE_FILLED : ICON_REMAINING} />}
							<span>{timeText}</span>
						</InfoBarTicker>
					)}
				</InfoBarCell>
			</InfoBar>

			<GameHover data-at="hover">
				<GameHoverTop>
					<GameName data-at="name">{name}</GameName>
					<ProviderName data-at="tags">{tournamentTags}</ProviderName>
				</GameHoverTop>

				<GameHoverBottom>
					<PlayButton onClick={goDetails} data-at="details-button">
						{translations[HOVER_BUTTON_TEXT_KEY]}
					</PlayButton>
				</GameHoverBottom>
			</GameHover>
		</Container>
	);
}
